'use client';

/**
 * **The AI review field-marking treatment, in one place** (VALE-1249).
 *
 * The treatment is the set of rules that say what a reader sees about a field
 * the AI touched. Before this module they were applied by two components — the
 * review list's contact detail block and the contact drawer — and each restated
 * the whole decision, ~180 lines apiece doing the same job. They had already
 * drifted once: a changed field was marked one way in the list and another in
 * the drawer until VALE-1208 reconciled them, and VALE-1247 had to reconcile
 * them again on the bank account row. A third and fourth copy were queued
 * (inventory, the deceased's information, intake review), which is what this
 * ticket stops.
 *
 * ## What is here, and what is deliberately not
 *
 * Here: everything about **how a marked row reads**. Ten rules, each named in
 * its own docblock — the item-kind gate, the row's contract keys, the field
 * kind, the unset-flag rule, the has-a-value rule, ink-versus-badge, the inline
 * superseded value, the hover popover and its keying, the reversible reject,
 * the clear affordance routing into it, and the reviewer's own edits.
 *
 * Not here: **which rows a surface renders at all.** The review list shows only
 * what the AI touched, because it is a summary of an extract; the drawer shows
 * every field, because it is a record editor. That is a property of the surface,
 * not of the treatment, and pushing it in here to make the seam look complete is
 * exactly what this ticket's scope warns against. What the two DO share is the
 * predicates underneath that filter — {@link isUnsetFlag} and
 * {@link isExtractedFlag} — so the predicates live here and the policy stays
 * with the list.
 *
 * Also not here: a surface's labels, its column count, its de-duplication
 * against a row above it, or its read-only fallback. Those differ because the
 * surfaces differ.
 *
 * ## Why a hook rather than a wrapper component
 *
 * The treatment has to own the hover state (rule 7) — one `openKey` shared by
 * every anchor on a surface — and state means a hook. A wrapper component was
 * the alternative and does not fit: the two consumers render *different* grid
 * components (`AttributeGrid` flat, `AttributeSections` grouped), so a wrapper
 * would have to abstract the grid as well, which is a far wider change than
 * extracting the treatment. A plain mapper function was the other alternative
 * and leaves rule 7 half-extracted, with each call site still wiring its own
 * `useChangeHover`.
 *
 * So: a hook that owns the state and hands back a mapper over one
 * `AttributeSpec`. A surface adopts the treatment by calling it and mapping its
 * own specs through the result.
 *
 * ## It is not contact-shaped
 *
 * Nothing here knows what a contact is. A row's contract keys are asked of the
 * row's own `toPatch`, the diff is a `ReviewItemStatus` keyed by whatever the
 * surface's diff is keyed by, and the popover scope is a caller-supplied string.
 * That is what lets an inventory grid, the deceased's information or intake
 * review bind to it without any of them being special-cased here — and it is
 * asserted directly, against a grid whose rows are not a contact's, in
 * `__tests__/ai-review-field-marking.test.tsx`.
 *
 * ## What a surface adopting this owes, and what it gets (VALE-1181)
 *
 * Written as rules because the first adoption discovered every one of them by
 * being told, one round at a time, and the next two should not have to be. Read
 * this before writing an adoption; the numbered rules below are the mechanics.
 *
 * · **A field is marked by COLOUR, and never by a tag.** One encoding for one
 *   fact. A tag is for an ITEM — the row line, the drawer header — where there
 *   is no value to colour. Rules 5, 5a and 5b between them make the colour reach
 *   a text value, a pill, a segmented control and a kept control alike, so a
 *   surface never needs a fallback of its own.
 * · **Every marked row gets the hover popover and the reject**, whatever it
 *   draws. If a row kind cannot carry them, that is a gap in this file and not
 *   something for the surface to work around: two of them were, and both are
 *   fixed here (5a, 5b).
 * · **A summary block renders VALUES, not controls.** Where a builder hands back
 *   a kept control for a record editor, a summary drops `render` and prints the
 *   value: a combobox in a column of label/value pairs reads as a different kind
 *   of thing at a different size, and editing belongs to the drawer the row
 *   opens anyway. The drawer keeps the control, and rule 5b marks it there.
 * · **A row's layout properties belong to the surface that declared them.**
 *   `fullWidth` and `startsRow` are decided for a 640px drawer with headed
 *   groups; a flattened two-column block strips both and carries `wraps`
 *   explicitly, since `AttributeReadOut` otherwise derives it from `fullWidth`.
 * · **De-duplicate against what the row line already states**, and let a repeat
 *   earn its row back when the AI changed it — the line can print a figure but
 *   cannot say it moved.
 * · **`toPatch` is a question this file ASKS during render.** A row whose patch
 *   is not a pure function of its draft must declare `AttributeSpec.diffKey`
 *   instead; see rule 2.
 *
 * ## The verdict from the first real adoption (VALE-1181)
 *
 * Inventory is the first surface that did not inform these rules, so it is the
 * first evidence about whether they are *rules* or a description of how contacts
 * happen to work. **Four of the nine fitted as written; four were wrong and are
 * fixed here; one is still untested — and a tenth was missing entirely.** Read
 * this before the next adoption rather than rediscovering it.
 *
 * · **Rule 1, the item-kind gate — fitted.** `skip` and a change-less `update`
 *   both fall through, and inventory's backend emits `skip` for the latter, so
 *   the case does not even arise on the wire.
 *
 * · **Rule 2, the contract keys — WRONG, and fixed.** It read `Object.keys` of
 *   the patch, which is one level, while `ReviewItemStatus.fieldChanges` is
 *   documented dot-notation. Every one of inventory's ~100 `info`-backed rows
 *   commits `{ itemInfo: { … } }` and answered `['itemInfo']`, so nothing
 *   matched and nothing marked. It now walks the patch as a tree, parent first.
 *   **The `mark(spec, keys)` hatch is not the alternative to this and must not
 *   be treated as one**: nesting is the diff contract's, shared by every
 *   surface; RENAMING is a surface's own, and the hatch is for that. The
 *   deceased's information needs both — it commits `decedentFirstName` against a
 *   diff keyed `firstName` (rename, hatch) and `{ decedentAddress: {…} }`
 *   against `address.streetAddress` (nesting AND rename, so the hatch again,
 *   handing in the dotted keys).
 *
 * · **Rule 3, a create reads as new — fitted**, unchanged.
 *
 * · **Rule 4, the two flag predicates — STILL UNTESTED.** The brief expected
 *   inventory to settle the `triState` asymmetry between {@link isUnsetFlag} and
 *   {@link isExtractedFlag}. It cannot: **inventory has no `triState` row at
 *   all** — 22 builders, and the flag kinds present are `boolean` (23 rows,
 *   almost all `Non-estate asset`) and nothing else. The two predicates agree on
 *   every `boolean`, so inventory exercises them and never separates them. The
 *   asymmetry is unchanged and still waiting for a surface that has one.
 *   Rule 4a itself fitted: without it every item would carry a
 *   `Non-estate asset — No` row.
 *
 * · **Rule 5, ink versus badge — half wrong, and fixed.** The ink/badge split
 *   itself fitted, including for a kind contacts do not have: a
 *   `valueWithConfirmation` row draws its figure as a NODE, so it takes the
 *   badge branch rather than the ink branch, which is that branch working as
 *   designed. What was wrong is that the badge **never reached the screen on an
 *   empty row** — the one case it exists for. See the wrapper below.
 *
 * · **Rule 6, the inline superseded value — wrong for scalars, and fixed.**
 *   `describeChangeValue` handled a contact's shapes (whole objects, booleans,
 *   strings) and printed everything else with `String`. Inventory's diff carries
 *   bare numbers and split object parts, so a money row printed `4400` struck
 *   beside its own `$4,600.00`, and a superseded BSB printed `013000` beside
 *   `062-000 · 12345678`. It now takes the row's key and editor kind. **One case
 *   remains open**: a changed key no row claims gets an appended read-out with
 *   no editor to ask, so a number there is still printed plainly.
 *
 * · **Rule 7, the popover and its keying — fitted.** Keying on the row label
 *   scoped by the item id is what inventory needs too, and more so: a
 *   `bank-account` row and its DOD row both write under `itemInfo`.
 *
 * · **Rule 5b, a row that draws its own control — MISSING, and added.** The
 *   institution, registry and ticker searches are `render` rows, and
 *   `AttributeGrid` hands them the whole value slot: no read-out, no badge, no
 *   `valueClassName`, no `markWrapper`. So the row that NAMES the bank was the
 *   one row on the surface that could not say the AI had changed it, and had no
 *   popover and no reject either. Contacts have no such row, which is why the
 *   extraction never saw it. 17 of the 22 inventory types do.
 *
 * · **Rules 8 and 9, the reject and the clear — fitted, once inventory had a
 *   reject of its own to fit them to.** They were nearly not exercised at all:
 *   `ReviewRejections` keys by the row's synthetic id and `pendingIdForIndex`
 *   mints the same id for contact 0 and inventory item 0, so one store could not
 *   serve both. Inventory got its own two maps instead. That is worth knowing
 *   before the next adoption, because the alternative was worse than it looks —
 *   **rule 9 is only safe where a reject exists.** With no reject it leaves the
 *   ordinary destructive `×` in place, and `offersClear` derives one for
 *   `bankAccount` and `valueWithConfirmation`, so a surface without a reject
 *   must render read-only rather than editable.
 *
 * · **Rule 8's REACH was wrong on both surfaces, and it is a finding about the
 *   rule rather than about either of them.** The treatment marks a rejected
 *   value where it draws it, and stops there — which reads as complete on a
 *   record editor, where the grid is the only place the value appears. On a
 *   LIST it is not: the row line above the block draws the same values, from
 *   the same proposed record, and a collapsed block leaves the row line as the
 *   only thing the reviewer is reading. Both adoptions got this wrong in the
 *   same shape. Contacts shipped a reject that struck nothing on the row at
 *   all, so a turned-down relationship or LPR chip went on reading as fact;
 *   inventory struck three parts and derived the title's key by restating
 *   `formatLineItemDisplay`'s type → key mapping as `'institution'`, which is
 *   right for 17 types and wrong for the two share types (the ticker), the
 *   vehicle (make and model) and four more (the description).
 *
 *   **The rule the next adoption should read as part of the set: a reject must
 *   stop the value being asserted EVERYWHERE the surface draws it, and each
 *   place asks the code that drew it which keys it used.** The treatment cannot
 *   own this — it never sees the row line — so it is the surface's, but it is
 *   not optional, and it is not something a surface should be left to discover.
 *   `formatLineItemDisplay` and `contactRowLineKeys` are the two worked
 *   examples: both return their own key mapping so a strike map binds to it
 *   instead of keeping a second copy that drifts.
 */

import { useCallback, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AttributeSpec } from '@/components/attributes';
import { cn } from '@/lib/utils';
import type { FieldChange } from '@/lib/services/ai-analysis';
import {
  ChangedValue,
  PreviousValue,
  changeInkClassName,
  changeInkDescendantClassName,
  REJECTED_VALUE_INK
} from './change-marks';
import { ChangePopover, useChangeHover } from './change-popover';
import { setNestedValue } from '@/components/documents/ai-import/review-card/field-change-utils';
import {
  describeChangeValue,
  fieldChangeFor,
  fieldChangeKind,
  itemChangeKind,
  rendersValueAsText,
  rowHoldsAValue,
  type FieldChangeKind,
  type ReviewItemStatus
} from './change-status';

/** The shape both predicates and the key derivation need — any attribute row. */
interface FlagLikeSpec {
  editor?: { kind?: string };
  draft?: unknown;
}

/** Whether this row draws its value as a flag control rather than as text. */
function isFlagRow(spec: FlagLikeSpec): boolean {
  const kind = spec.editor?.kind;
  return kind === 'boolean' || kind === 'triState';
}

/**
 * **The non-text editor kinds rule 5 still INKS rather than badging** — beside
 * the flag rows {@link isFlagRow} names, which take the ink for the same reason.
 *
 * Named and exported (VALE-1363) rather than left as a chain of `===` inside the
 * mapper, because "does a money figure take the ink or the badge?" is a question
 * a surface adopting this treatment asks — the AI-extract review's inventory
 * block asks it of every one of the 22 item types — and answering it by reading
 * the middle of a closure is how a second, drifting answer gets written.
 *
 * A `select` draws a status pill and a segmented control a lit segment; the two
 * money kinds draw their amount as a node. All four are things a colour can
 * reach, so {@link rendersValueAsText} reading them as un-inkable is about the
 * READ-OUT path, not about whether ink lands — `valueClassName` goes on the cell
 * the node renders inside, and the value inherits it. The badge is the fallback
 * for a row nothing can be inked on at all, which since rule 5a means a row
 * with no value.
 */
export const INKABLE_EDITOR_KINDS: readonly string[] = [
  'select',
  'valueWithConfirmation',
  'marketFigure'
];

/**
 * **Rule 4a — a flag the RECORD does not hold** (VALE-977, VALE-1208).
 *
 * A form is a full record editor, so it supplies a row for every flag a record
 * CAN have, and an unheld `boolean` renders `No`. That is a string, so a naive
 * "does this row have a value" test passes it and puts `Minor No`,
 * `Deceased No`, `Verified No` under every row of a summary that exists to show
 * what the extraction actually found.
 *
 * A `triState` is different and deliberately so: its unheld state is `unknown`,
 * and an explicit `no` is an answer the record holds. Only `unknown` is absent.
 *
 * This is the **selection** question — *is there anything here to show?* — and
 * the review list's changed-only filter is its one caller. It lives here rather
 * than in the list because {@link isExtractedFlag} answers the neighbouring
 * question and the two must be read together (see that docblock).
 */
export function isUnsetFlag(spec: FlagLikeSpec): boolean {
  const kind = spec.editor?.kind;
  if (kind === 'boolean') return spec.draft !== 'yes';
  if (kind === 'triState') return spec.draft === 'unknown';
  return false;
}

/**
 * **Rule 4b — a flag the EXTRACTION actually spoke to** (VALE-1208).
 *
 * On a created item every field reads as new (rule 3), but a flag the
 * extraction never mentioned still renders its default, and inking that claims
 * the AI said something it did not. A flag counts as extracted only when the
 * diff names it, or when it is affirmatively set.
 *
 * This is the **marking** question — *did the AI say this?* — and the mapper
 * below is its one caller.
 *
 * **It is not the same test as {@link isUnsetFlag}, and the difference is real.**
 * They agree on every `boolean`. They disagree on a `triState` drafted `no`:
 * the record holds that answer, so selection keeps the row, but this test reads
 * it as un-spoken-to and leaves it unmarked. That asymmetry is carried across
 * from the two implementations verbatim rather than reconciled here — VALE-1249
 * is an extraction, and reconciling it would be a behaviour change on both
 * surfaces under a ticket whose contract is that neither moves. It is named
 * rather than buried precisely so the next reader can decide it on purpose; the
 * pull request carries it as an unhandled member.
 *
 * **Inventory was expected to settle it and could not** (VALE-1181): across all
 * 22 builders it has no `triState` row, only `boolean` ones, and the two
 * predicates agree on every `boolean`. So the adoption exercised both and never
 * separated them. Still open, and still waiting for a surface that has one.
 */
export function isExtractedFlag(
  spec: FlagLikeSpec,
  namedByTheDiff: boolean
): boolean {
  if (!isFlagRow(spec)) return true;
  return namedByTheDiff || spec.draft === 'yes';
}

/**
 * Whether a patch value is a plain object whose keys are worth walking into.
 *
 * An array is not: a patch carrying `transferBeneficiaries: […]` commits the
 * list as one value, and the diff keys it as one value, so indexing into it
 * would invent keys nothing produces. A `Date` and a class instance are not,
 * for the same reason.
 */
function isWalkableObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Every key a patch stands for, **parent before its leaves**.
 *
 * `{ itemInfo: { bsb } }` → `['itemInfo', 'itemInfo.bsb']`. Both, not one or
 * the other, because which of the two a diff uses is the diff's business and
 * differs per surface — a contact's `bankAccount` is diffed whole under the
 * parent key, an inventory item's `itemInfo.bsb` under the leaf.
 *
 * **Parent first is load-bearing, not cosmetic.** {@link AiReviewFieldMarking.mark}
 * takes the FIRST key that carries a change and files the reject under
 * `rowKeys[0]` on a created item, so a leaf jumping ahead of its parent would
 * silently re-key both on every existing contact row.
 */
export function patchKeyPaths(
  patch: Record<string, unknown>,
  prefix = ''
): string[] {
  const paths: string[] = [];
  for (const [key, value] of Object.entries(patch)) {
    const path = prefix ? `${prefix}.${key}` : key;
    paths.push(path);
    if (isWalkableObject(value)) paths.push(...patchKeyPaths(value, path));
  }
  return paths;
}

/**
 * **The one of a row's keys that IS the row**, for a created item's reject.
 *
 * `'whole-patch'` takes the parent, which is what a row sent whole under its own
 * key means. `'leaf'` takes the deepest path, which is what a row writing into a
 * container shared with its siblings means.
 *
 * **A row whose patch writes SEVERAL sibling leaves has one row and one flag**,
 * so `'leaf'` files under the first of them. That is the row's own choice of
 * granularity and not the collision this exists to prevent — the collision was
 * between DIFFERENT rows sharing a container key, which is what nulled an
 * asset. It does leave a real limit, and the limit is not hypothetical:
 * `withoutRejectedFields` drops only the leaf named, so such a row's other
 * committed keys still apply. **Nine rows across five inventory types do this**
 * — `bank-account`'s `Bank account` and `DOD value`, five of
 * `nursing-home-bond`'s figures, and the shared `Property address` — measured
 * and listed in `compact/__tests__/every-inventory-type-renders.test.tsx`,
 * which fails on a tenth rather than letting it ship quietly.
 */
export function ownKeyOf(
  rowKeys: string[],
  policy: 'whole-patch' | 'leaf'
): string | undefined {
  if (policy === 'whole-patch' || rowKeys.length === 0) return rowKeys[0];
  let deepest = rowKeys[0];
  let depth = -1;
  for (const key of rowKeys) {
    const keyDepth = key.split('.').length;
    if (keyDepth > depth) {
      depth = keyDepth;
      deepest = key;
    }
  }
  return deepest;
}

/**
 * **Rule 2 — which contract keys a row stands for, asked of the row itself.**
 *
 * `toPatch` IS the statement of what a field commits, so reading its output
 * keys cannot drift from the row: a `Name` row that emits first/middle/last
 * lights up for a change to any of them, and an `Address` row that emits one
 * object under one key matches the diff that carries it that way.
 *
 * **It reads the patch as a tree, not as one level** (VALE-1181). The diff's own
 * contract says so — {@link ReviewItemStatus.fieldChanges} is documented "Keys
 * are dot-notation" — and reading only `Object.keys` contradicted it the moment
 * a surface with a nested patch arrived. Inventory is that surface: every
 * `info`-backed row commits through `infoPatch`, which returns
 * `{ itemInfo: { bsb } }`, so the derivation answered `['itemInfo']` for EVERY
 * such row while the diff keyed the same field `itemInfo.bsb`. Nothing matched,
 * so nothing marked. Contacts never showed it because every contact key is flat
 * — which is exactly the blind spot VALE-1249 warned its rules might carry, the
 * treatment having been derived from two surfaces that both render a contact.
 *
 * Nesting is a property of the **diff contract**, shared by every surface, which
 * is why it is answered here. **Renaming is not, and is what
 * {@link AiReviewFieldMarking.mark}'s `keys` argument is for**: the deceased's
 * information commits `decedentFirstName` and is diffed under `firstName`, and
 * no walk of the patch can bridge that — only the surface knows. The two escapes
 * are not alternatives; one belongs here and one belongs to the caller.
 *
 * A row whose patch cannot be built from its own draft marks nothing, rather
 * than throwing through a render.
 */
export function contractKeysFor(spec: {
  toPatch?: (draft: never) => unknown;
  draft?: unknown;
  diffKey?: string | readonly string[];
  readKey?: string;
}): string[] {
  /*
    **A declared key is taken as given, and `toPatch` is never called**
    (VALE-1181). See {@link AttributeSpec.diffKey}: asking the question is not
    free on every row. One inventory row resets host state inside its own
    `toPatch` — the only hook it has for the un-tick transition — so the
    speculative call fired a `setState` mid-render and the loop that followed
    took the review screen down with "Too many re-renders".

    This is the one real limit rule 2 has, and it is a limit on the MECHANISM
    rather than on the rule: reading a row's keys off its patch cannot drift
    from the row, which is why it is worth keeping, but it presumes the patch is
    a pure question and nothing enforces that. A row that knows the presumption
    does not hold says so instead.
  */
  /*
    **A list, because one row can own several keys** (VALE-1597). The shares
    figure rows commit a value, its per-share price and the market-sourced
    marker in one patch; declaring only one of them would leave the diff's other
    keys unclaimed and back on the read-only path this escape hatch exists to
    keep them off. An empty array falls through to the `toPatch` walk rather
    than declaring nothing, so a builder that computes its list and comes up
    empty degrades to the default instead of silently muting the row.
  */
  if (spec.diffKey) {
    const declared =
      typeof spec.diffKey === 'string' ? [spec.diffKey] : [...spec.diffKey];
    if (declared.length > 0) return declared;
  }

  try {
    const patch = spec.toPatch?.(spec.draft as never);
    const written = patch
      ? patchKeyPaths(patch as Record<string, unknown>)
      : [];
    /*
      **`readKey` ADDS, where `diffKey` REPLACES** (VALE-1375), and the
      difference is why it is a second member rather than a second caller of the
      first.

      `diffKey` answers "do not ask this row's `toPatch`" — it exists because one
      inventory row resets host state inside its patch, so the speculative call
      took the screen down. It is a statement about the MECHANISM.

      `readKey` answers "the diff calls this row something other than what its
      patch writes", which is a statement about the row. `Minor` is the only one
      on a contact: it commits `isOfAge` and is diffed under `isMinor`. Both keys
      have to survive, because two different questions are asked of this list —
      the diff lookup wants the key the AI used, and the reviewer's-own-edits
      lookup wants the key a commit wrote, since that is what `editedKeys`
      records. Declaring `diffKey: 'isMinor'` instead was tried and dropped the
      row from the block the moment a reviewer touched it: the change matched,
      and the edit no longer did.

      The read key goes FIRST, because `changeFor` takes the first key that
      carries a change and the AI's key is the one that will.
    */
    return spec.readKey ? [spec.readKey, ...written] : written;
  } catch {
    return [];
  }
}

/** The diff entry a row carries, and the key it was found under. */
export interface RowChange {
  key: string;
  change: FieldChange;
}

export interface AiReviewFieldMarkingOptions {
  /**
   * What the AI did to this item.
   *
   * **Absent is the whole no-diff case**: {@link AiReviewFieldMarking.mark}
   * becomes the identity function, so a surface with nothing to diff against
   * renders exactly as it always has.
   */
  status?: ReviewItemStatus;
  /**
   * Per-key reject flags in the `acceptedChanges` contract the apply payload
   * already carries — `false` means rejected.
   */
  acceptedChanges?: Record<string, boolean>;
  /**
   * Toggle one field's reject. Omit to offer no reject.
   *
   * `clearTo` is what this field becomes once rejected, and it is supplied only
   * where the ROW's editor knows better than the payload can (VALE-1375): a
   * flag's cleared state is `false` for a plain boolean and unanswered for a
   * TRI-STATE, and nothing in the payload tells those apart. Hosts that keep no
   * cleared-value map may ignore the second argument.
   */
  onToggleRejectChange?: (key: string, clearTo?: unknown) => void;
  /**
   * What makes this surface's popover keys unique (rule 7).
   *
   * The key is `<scope>:<row label>`, and the **label** half is not negotiable:
   * four bank rows all write the same `bankAccount` key, so keying on the
   * contract key gave them one popover id between them and hovering the BSB
   * opened the account number's popover at the same time (VALE-1208). The scope
   * half is the surface's, because a list renders one of these blocks per row
   * and the drawer renders one full stop.
   */
  popoverScope: string;
  /**
   * **Which of a row's contract keys IS the row, when the diff has no change to
   * name one** (VALE-1181). A created item has no `FieldChange` anywhere, so
   * every reject on it is filed under a key derived here.
   *
   * It has to be declared because **the shape cannot decide it**. A contact's
   * `bankAccount` row and an inventory `info` row produce structurally
   * identical key lists — `['bankAccount', 'bankAccount.bsb', …]` and
   * `['itemInfo', 'itemInfo.bsb']` — and want opposite answers:
   *
   * · `'whole-patch'` (the default, contacts): the row commits one object under
   *   its OWN key and is turned down as a whole, so the parent is the row.
   * · `'leaf'` (inventory): the row commits into a container it SHARES with
   *   every sibling row, so the parent is not the row — it is the container.
   *   The deepest path is.
   *
   * Getting it wrong is not cosmetic and is not visible in a suite. Inventory
   * ran on the default for one branch: every `info`-backed row on a created
   * item filed under the bare `itemInfo`, so rejecting an asset's SRN also
   * struck its share count, and `withoutRejectedFields` — which nulls a flat
   * key and deletes a dotted one — set `itemInfo = null` at submit, discarding
   * every field of the asset the reviewer had turned ONE field down on.
   */
  rejectKeyPolicy?: 'whole-patch' | 'leaf';
  /**
   * **Rule 10 — the contract keys the REVIEWER has changed on this item**
   * (VALE-1264).
   *
   * The AI's diff is a statement about the extract as it arrived, and it never
   * learns about an edit made while the extract is being reviewed. For most
   * rows that costs nothing: a text row the reviewer types into still has a
   * value, so it is still selected and still reads as whatever the AI made it.
   * A **flag** is the exception, because both flag questions are answered from
   * the draft alone — {@link isUnsetFlag} for whether the row is admitted,
   * {@link isExtractedFlag} for whether it is marked — and turning a flag off
   * is indistinguishable from a flag nobody ever set.
   *
   * So answering `Beneficiary` **No** on a created contact deleted the row: the
   * reviewer's own edit removed the only thing they could confirm or reject it
   * with, and left no record that the field had been touched at all. Both
   * altitudes take this set, which is what makes them agree.
   *
   * Absent is the ordinary case — the contact drawer outside a review, and any
   * surface with no commit path of its own — and then nothing here changes.
   */
  editedKeys?: ReadonlySet<string>;
  /**
   * **Put the AI's own value back when a reject is taken back** (VALE-1617).
   *
   * A reject never touches the buffer: it is applied at commit, by clearing the
   * key. So taking one back only stops the clearing, and whatever the buffer
   * holds is what applies — which is the AI's value if the reviewer left it
   * alone, and the reviewer's own edit if they did not. `Use suggested value`
   * therefore reinstated the reviewer's edit on exactly the path where they
   * most needed the suggestion back, and said the opposite while doing it.
   *
   * The treatment hands back a patch naming the diff key and the AI's proposed
   * value; the host writes it through the same commit path a row edit uses, so
   * the un-reject and the reject-clearing-itself land in one gesture.
   *
   * **Scalar rows only.** A picker resolves a relation, a flag has its own
   * draft shape, and a composed money row commits parts — none can be rebuilt
   * from a diff value alone, so those keep today's behaviour and say `Undo
   * reject` instead ({@link rejectActionLabel}). Omit this and every row keeps
   * today's behaviour, wording included.
   */
  onRestoreSuggested?: (patch: object) => void;
  /**
   * **What each edited key held before the reviewer first touched it**
   * (VALE-1617) — on a CREATED item, the value the AI supplied.
   *
   * {@link onRestoreSuggested} restores from the diff's `proposed`, and a
   * created item has no `FieldChange`: the extract IS the proposal. So without
   * this, `Use suggested value` could not restore anything on the commonest
   * path in a review, and reinstated the reviewer's own edit instead.
   *
   * Supplied by the host's edits store (`useReviewFieldEdits.priorsFor`), which
   * remembers only the FIRST prior per key — a later edit's prior is the
   * reviewer's own earlier edit, and handing that back would restore a value
   * the AI never proposed.
   */
  originalValues?: ReadonlyMap<string, unknown>;
  /**
   * **Whether there is anything here to review** (VALE-1617).
   *
   * A row the REVIEWER added is genuinely new — the record is being created, so
   * the badge and the change ink tell the truth about it and must stay. What it
   * has is no *suggestion*: nobody proposed these values, so a hover card
   * offering to accept or reject one is an affordance for a decision that does
   * not exist. It read as the product asking the reviewer to review their own
   * typing.
   *
   * `false` keeps every mark and drops the popover and the reject with it. The
   * alternative — withholding the item's status so the treatment does nothing —
   * was tried and is wrong: it takes the ink and the badge too, and a row the
   * reviewer just added then reads as an ordinary untouched record.
   */
  offersReview?: boolean;
  /**
   * **Whether a note ADDITION prints the note it lands after** (VALE-1951).
   *
   * A drawer is the whole record, so it prints the note as it will be saved:
   * the recorded note, then the addition beneath it. A compact review row
   * prints the addition alone behind a muted `…`, which says there is more
   * note before it without spending the row on repeating it. Defaults to
   * `false`, the row's form.
   */
  showsRecordedNote?: boolean;
}

export interface AiReviewFieldMarking {
  /**
   * Apply the treatment to one row.
   *
   * `keys` is optional and exists for a row the surface built itself: a
   * read-only row appended below the form's own has no `toPatch` to read, so it
   * would match no change and go unmarked. A row that came from a spec builder
   * needs nothing — its keys are derived.
   */
  mark: <TPatch>(
    spec: AttributeSpec<TPatch>,
    keys?: string[]
  ) => AttributeSpec<TPatch>;
  /**
   * The diff entry this row carries, if any — the first of its keys the AI
   * touched (rule 2).
   *
   * Handed back because a surface deciding *whether to render* a row asks
   * exactly this, and asking it through the treatment is what stops a second
   * key-derivation growing in the surface. The review list's changed-only
   * filter is the current caller.
   */
  changeFor: (
    spec: { toPatch?: (draft: never) => unknown; draft?: unknown },
    keys?: string[]
  ) => RowChange | undefined;
  /** Whether this item has anything to mark at all (rule 1). */
  showsChanges: boolean;
  /**
   * **Rule 3 — every field of a created item reads as new.**
   *
   * A create carries no `fieldChanges`: the item IS the change. Surfaces need
   * this for their own selection too, which is why it is handed back rather
   * than staying private.
   */
  itemIsNew: boolean;
  /**
   * **Rule 10 — whether the reviewer has changed this row themselves**
   * (VALE-1264), read off the row's own contract keys exactly as
   * {@link changeFor} reads the diff.
   *
   * Handed back for the same reason `changeFor` is: the surface's selection
   * filter has to ask it, and asking it through the treatment is what keeps the
   * two altitudes answering from one set. See
   * {@link AiReviewFieldMarkingOptions.editedKeys}.
   */
  wasEdited: (
    spec: { toPatch?: (draft: never) => unknown; draft?: unknown },
    keys?: string[]
  ) => boolean;
  /** Close any open popover — for a host committing a value out from under one. */
  closeHover: () => void;
}

/** Nothing edited — a stable identity, so the mapper is not rebuilt each render. */
const NO_EDITED_KEYS: ReadonlySet<string> = new Set();

/**
 * Stable identity for "nothing rejected", for the same reason
 * {@link NO_EDITED_KEYS} has one: a fresh `{}` default mints a new object every
 * render, so `mark` and the hook's own `useMemo` never memoise for a caller
 * that passes nothing — which is every drawer opened away from a review.
 */
const NO_REJECTED_CHANGES: Record<string, boolean> = {};

/**
 * **The popover's anchor, which closes itself as it goes** (VALE-1264).
 *
 * A component rather than a bare span for one reason: an unmount hook. The grid
 * swaps a row's read-out for its editor the instant the reader clicks it, and
 * an unmount fires no `mouseleave` — so the hover state was left naming a row
 * that had left the screen, and clicking out of the editor re-mounted the
 * anchor into a still-matching `openKey`. The popover reappeared with the
 * pointer nowhere near it.
 *
 * **It hugs what the row actually drew.** `inline-flex` rather than `flex`,
 * because a block-level flex box fills its parent: on a text row this anchor
 * sits INSIDE the value button, which is `flex-1` across the whole value
 * column, so hovering the empty space to the right of a name opened its
 * popover. Measured on the review block at 1400px, `Name` drew ~110px of text
 * inside a 208px trigger. `max-w-full` keeps the cap the truncation needs —
 * without it the shrink-to-fit box sizes to `max-content` and a long address
 * overflows its button instead of ellipsising inside it.
 *
 * A control row's anchor already hugged (a 55px pill in a 65px trigger), so
 * this changes nothing there — which is the point of the trigger being derived
 * from the drawn mark rather than declared per row kind.
 *
 * **Exported only for its own spec.** A surface still adopts the treatment
 * through {@link useAiReviewFieldMarking} and never renders this itself. The
 * ref-forwarding below is the one property of it that no test reaching through
 * the hook can see — see that prop's docblock — so the spec addresses it
 * directly rather than asserting something adjacent and calling it covered.
 */
export function ChangeAnchor({
  ref,
  popoverKey,
  onOpen,
  onClose,
  closeIfOpen,
  fill = false,
  children
}: {
  /**
   * **`PopoverAnchor asChild` hands its ref to this component, and the span is
   * what has to receive it** — the element Radix measures to place the card.
   *
   * It was a bare `<span>` before, which took the cloned ref for free; making
   * it a component to own the unmount hook silently swallowed it. React 19
   * passes `ref` as an ordinary prop rather than warning, so nothing failed:
   * the popover still mounted, still hovered, still carried its reject — and
   * rendered at the viewport origin with no anchor to sit beside, which reads
   * as the per-field popover having been deleted.
   *
   * Neither jsdom nor a DOM-presence check can see this: both say the anchor
   * and the card exist, which they do. Only where the card lands says
   * otherwise, so the regression test asserts the ref reaches the span.
   */
  ref?: React.Ref<HTMLSpanElement>;
  popoverKey: string;
  onOpen: () => void;
  onClose: () => void;
  closeIfOpen: (key: string) => void;
  /**
   * **Fill the value cell instead of hugging the mark** (VALE-1181).
   *
   * The default hugs, which is right for a text row: VALE-1264 measured a
   * `Name` row drawing 110px of text inside a 208px trigger, and `inline-flex`
   * is what pulled the trigger back onto what the row actually draws.
   *
   * A `render` row inverts it. What that row draws IS the whole control, and a
   * control is expected to occupy the value cell exactly as every other field's
   * value target does — so hugging shrink-wrapped an institution picker to its
   * own text, 188px inside a 257px cell, and made its width track its content:
   * narrow on a short name, out to the column edge on a long one. It also left
   * the control's `truncate` nothing to truncate against, so a long name grew
   * the box rather than ellipsising.
   */
  fill?: boolean;
  children: ReactNode;
}) {
  useEffect(() => () => closeIfOpen(popoverKey), [popoverKey, closeIfOpen]);

  return (
    <span
      ref={ref}
      data-testid="ai-change-anchor"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      className={cn(
        'min-w-0 items-center gap-1.5',
        /*
          The control is wrapped in markup this file does not own, so filling
          the anchor is not enough — its child has to be told to take the room.
          First child only: an updated control row also carries its superseded
          value in here, and stretching that would split the cell in half.
        */
        /*
          **Every child EXCEPT the notes**, rather than "the first one"
          (VALE-1690). A rejected row now puts the value that will apply ahead
          of what the row drew, so the control is no longer first and the old
          selector stretched the note instead — splitting the cell and shrink-
          wrapping the picker, which is the shape VALE-1264 fixed. The notes
          carry `data-ai-change-note`; whatever is left is the value.
        */
        fill
          ? 'flex w-full [&>*:not([data-ai-change-note])]:min-w-0 [&>*:not([data-ai-change-note])]:flex-1'
          : 'inline-flex max-w-full'
      )}
    >
      {children}
    </span>
  );
}

/**
 * Bind a surface to the AI review field-marking treatment.
 *
 * ```tsx
 * const { mark, showsChanges } = useAiReviewFieldMarking({
 *   status,
 *   acceptedChanges,
 *   onToggleRejectChange,
 *   popoverScope: `${item.id}`
 * });
 *
 * const fields = specs.map((spec) => mark(spec));
 * ```
 */
/** Nothing remembered — a stable identity, so the mapper's memo does not thrash. */
const NO_ORIGINAL_VALUES: ReadonlyMap<string, unknown> = new Map();

export function useAiReviewFieldMarking({
  status,
  acceptedChanges = NO_REJECTED_CHANGES,
  onToggleRejectChange,
  popoverScope,
  rejectKeyPolicy = 'whole-patch',
  editedKeys = NO_EDITED_KEYS,
  onRestoreSuggested,
  originalValues = NO_ORIGINAL_VALUES,
  offersReview = true,
  showsRecordedNote = false
}: AiReviewFieldMarkingOptions): AiReviewFieldMarking {
  const { openKey, hoverOpen, hoverClose, closeIfOpen } = useChangeHover();

  /*
    **Rule 1 — the item-kind gate.** `unchanged` covers both the no-status case
    and a matched item the AI did not touch, and in both the treatment is off
    entirely rather than rendering a marking that says "nothing happened".
  */
  const itemKind = itemChangeKind(status);
  const showsChanges = itemKind !== 'unchanged';
  const itemIsNew = itemKind === 'new';

  const changeFor = useCallback(
    (
      spec: { toPatch?: (draft: never) => unknown; draft?: unknown },
      keys?: string[]
    ): RowChange | undefined => {
      for (const key of keys ?? contractKeysFor(spec)) {
        const change = fieldChangeFor(status, key);
        if (change) return { key, change };
      }
      return undefined;
    },
    [status]
  );

  const wasEdited = useCallback(
    (
      spec: { toPatch?: (draft: never) => unknown; draft?: unknown },
      keys?: string[]
    ): boolean =>
      (keys ?? contractKeysFor(spec)).some((key) => editedKeys.has(key)),
    [editedKeys]
  );

  const mark = useCallback(
    <TPatch,>(
      spec: AttributeSpec<TPatch>,
      keys?: string[]
    ): AttributeSpec<TPatch> => {
      if (!showsChanges) return spec;

      const rowKeys = keys ?? contractKeysFor(spec);
      const found = changeFor(spec, rowKeys);
      const edited = wasEdited(spec, rowKeys);

      /*
        Rule 3: a create's fields carry no `FieldChange`, so they read as new.

        **Rule 10's marking half.** A key the reviewer changed themselves is a
        change worth showing even where the diff is silent about it, and on an
        item the AI merely updated the honest kind is `updated` — the record
        holds one value and is about to hold another (VALE-1264).
      */
      const kind: FieldChangeKind = found
        ? fieldChangeKind(found.change)
        : itemIsNew
          ? 'new'
          : edited
            ? 'updated'
            : 'unchanged';
      if (kind === 'unchanged') return spec;

      /*
        **Rule 5a — a row with no value takes no BADGE and no INK** (VALE-1264),
        and keeps its popover. Read below, where the two are applied; it is
        derived here because the badge branch and the ink branch both need it.
      */
      const holdsValue = rowHoldsAValue(spec);

      /* Rule 4b — a default is not an extracted value, unless the reviewer set it. */
      if (!isExtractedFlag(spec, !!found || edited)) return spec;

      /*
        The reject is filed under the changed key when there is one, and
        otherwise under the key the surface's policy says IS this row — a
        created item's field has no `FieldChange` at all, and it still has to be
        rejectable. See {@link AiReviewFieldMarkingOptions.rejectKeyPolicy} for
        why that second answer cannot be inferred from the keys themselves.
      */
      const rejectKey = found?.key ?? ownKeyOf(rowKeys, rejectKeyPolicy);

      /*
        Rule 6: the pre-AI value, in the form this surface prints values.

        The row's own key and editor kind travel with it (VALE-1181) — a number
        under a `money` row is currency and a string under `itemInfo.bsb` is a
        BSB, and neither is knowable from the value alone. Contacts pass through
        unchanged: their scalars are strings and dates, and their bank account
        and address arrive as whole objects, which are the branches that were
        already there.
      */
      /*
        An ADDITION carries the note it lands after too (VALE-1951): the row
        prints the note as it will be saved, recorded text then the addition.
      */
      const previous =
        (kind === 'updated' || kind === 'added') &&
        found &&
        spec.showsSupersededValue !== false
          ? describeChangeValue(found.change.existing, {
              key: found.key,
              editorKind: spec.editor?.kind
            })
          : undefined;

      /*
        **Rule 8 — the flag alone says rejected**, not the flag AND a
        `FieldChange`. A created item carries no `FieldChange`, so requiring one
        meant a create's field could be rejected and never look it: the control
        was there, the click registered, and nothing on screen or at apply
        changed (VALE-1208).
      */
      const rejected = !!(rejectKey && acceptedChanges[rejectKey] === false);

      /**
       * **What a rejected FLAG becomes, in both the payload and the control**
       * (VALE-1375).
       *
       * A flag is the one row kind whose cleared state is a real, drawable
       * value rather than an absence, and the two flag kinds do NOT clear to
       * the same thing. A plain boolean clears to `no`. A tri-state clears to
       * *unanswered*, and it matters: `isProvingLpr` is a tri-state precisely
       * because a flat `No` there is "a determination nobody had made", and
       * committing one writes `nonProvingReason: 'leave_reserved'`, inventing a
       * reason for a decision that was never taken.
       *
       * The payload cannot tell them apart — both are booleans on the wire — so
       * the row says it here, where the editor kind is known, and the store
       * carries it to the apply path.
       */
      const flagClear = isFlagRow(spec)
        ? spec.editor?.kind === 'triState'
          ? { draft: spec.editor.unsetValue ?? 'unknown', value: null }
          : { draft: 'no', value: false }
        : undefined;

      /**
       * The flag the AI asked for, once the control has moved off it.
       *
       * Stated only when it DIFFERS from what will apply — an AI that proposed
       * the same answer the reject clears to has nothing superseded to show,
       * and a struck `No` beside an unlit control is noise.
       */
      /**
       * **The AI's answer is struck WHERE THE ROW DRAWS IT** (VALE-1375).
       *
       * A first pass stated it as a second, struck value beside the control,
       * which put the same fact on screen twice and read as clutter. A
       * segmented control already draws both answers, so the superseded one is
       * struck in place: the control lights what will apply and the segment the
       * AI asked for is crossed out next to it, which is the toggle-and-strike
       * the reviewer expected.
       *
       * **Plain booleans only.** The strike is expressed as "the unlit
       * segment", which is exactly the AI's answer when one of two segments is
       * lit. A TRI-STATE rejected to unanswered lights neither, so the same
       * selector would strike both and claim the reviewer turned down an answer
       * they never gave.
       */
      /**
       * **The AI's value for this row, wherever it is recorded.**
       *
       * An UPDATED item carries it on the diff. A CREATED one does not — the
       * extract is the proposal — so it comes from what the key held before the
       * reviewer first overwrote it (VALE-1617). `undefined` when neither
       * remembers, and then there is nothing honest to restore.
       */
      const suggestedValue = found
        ? found.change.proposed
        : rejectKey && originalValues.has(rejectKey)
          ? originalValues.get(rejectKey)
          : undefined;

      const strikeSupersededSegment =
        rejected &&
        !!flagClear &&
        spec.editor?.kind === 'boolean' &&
        /*
          **Nothing to strike when the AI asked for what the reject lands on** —
          and the AI's answer is {@link suggestedValue}, never the row's CURRENT
          draft (VALE-1617).

          The created-item branch read `spec.draft === 'yes'`, which is the
          value the control is showing right now. The moment the reviewer had
          already answered `No` themselves, that read `false` and the strike was
          withheld — so rejecting a flag they had turned off did nothing visible
          at all except recolour the label, on the one surface built to say what
          is being turned down. An updated item was fine, because the diff
          carried the proposal; a create had nothing to read until the edits
          store began remembering the value each key held before the reviewer
          first touched it.
        */
        (suggestedValue !== undefined
          ? suggestedValue !== false
          : spec.draft === 'yes');

      /**
       * **Whether this row's value can be rebuilt from the diff alone**
       * (VALE-1617) — which is what decides both whether un-rejecting restores
       * the AI's suggestion and whether the action may say so.
       *
       * A scalar row's diff value IS its stored value: `itemInfo.bsb` holds the
       * string the row prints, so `{ itemInfo: { bsb: proposed } }` is a
       * complete, correct patch. Three kinds are not like that, and each fails
       * differently rather than merely awkwardly:
       *
       * · a **picker** (`render`) writes a RELATION — an institution's name is
       *   not its id, and resolving one is `resolveInstitutionUpdateFields`'
       *   job, so writing the diff's string would name an institution the
       *   record cannot resolve;
       * · a **flag** answers from its own draft shape, and a tri-state's
       *   unanswered is not the boolean the diff carries ({@link flagClear}
       *   exists for exactly that distinction);
       * · a **composed money row** commits its PARTS and lets the total be
       *   derived, so writing the total back would leave the parts stating a
       *   different figure.
       *
       * `found` is required because there is no suggestion to restore without
       * one — a created item's fields carry no `FieldChange`, and there the
       * buffer already holds what the AI supplied.
       */
      /**
       * **A FLAG is restorable too** (VALE-1617, corrected).
       *
       * It was excluded on the reasoning that "a flag has its own draft shape",
       * and that is true of the draft and irrelevant to the PATCH: a flag's
       * patch is the boolean at its own key, which is exactly what the diff
       * carries. The exclusion cost the commonest case of all — the AI proposes
       * `Yes`, the reviewer clicks to `No`, then takes it back and the control
       * cannot move, so the action recoloured the row and did nothing.
       *
       * Guarded on the suggested value actually BEING a boolean rather than on
       * the row kind: {@link flagClear} exists because a tri-state clears to
       * `null` where a plain boolean clears to `false`, and restoring is not
       * clearing — what goes back is whatever the AI answered.
       */
      const suggestionIsFlag =
        !!flagClear && typeof suggestedValue === 'boolean';

      const suggestionIsScalar =
        suggestedValue !== undefined &&
        !spec.render &&
        (!flagClear || suggestionIsFlag) &&
        !(
          spec.editor?.kind === 'valueWithConfirmation' &&
          !!spec.editor.components
        );

      /**
       * **Whether taking the reject back genuinely reinstates the AI's value.**
       *
       * True when nothing has overwritten it — the buffer still holds the
       * suggestion, so clearing the flag is all that is needed — or when the
       * row is scalar enough for {@link reject} to write it back. False only
       * for a row the reviewer has edited AND whose value cannot be rebuilt,
       * and there the action says `Undo reject` rather than promising a
       * restore it will not perform.
       */
      const restoresSuggested =
        !edited || (suggestionIsScalar && !!onRestoreSuggested);

      /**
       * Whether *this* click is the restoring one — rejected, overwritten, and
       * on a row the treatment can rebuild. Separate from
       * {@link restoresSuggested}, which is what the LABEL may promise and is
       * also true when there is simply nothing to restore.
       */
      const restoreSuggested =
        rejected && edited && suggestionIsScalar && !!onRestoreSuggested;

      const reject =
        offersReview && rejectKey && onToggleRejectChange
          ? () => {
              hoverClose();
              /*
                **Taking a reject back puts the AI's value back** (VALE-1617),
                so the reviewer's own overwrite does not quietly survive a click
                that offered them the suggestion. Only when they actually
                overwrote it: with the buffer already holding the suggestion
                this would be a redundant write, and every commit here re-renders
                both the block and the drawer above it.

                **The write IS the un-reject, and must not be followed by one.**
                Both hosts wrap their commit with rule 10's
                "answering a rejected field takes the reject back" — it matches
                on `keysTouchedByPatch`, and this patch names exactly
                `rejectKey`, so the flag is already clear by the time the commit
                settles. Toggling as well would flip it straight back on and the
                row would read as rejected after a click that said the opposite:
                two un-rejects are a reject.
              */
              if (restoreSuggested && rejectKey) {
                /* `setNestedValue` mutates and returns void — it is the apply
                   path's own dot-path writer, bound rather than restated so a
                   nested `itemInfo` key is built one way. */
                const patch: Record<string, unknown> = {};
                setNestedValue(patch, found?.key ?? rejectKey, suggestedValue);
                onRestoreSuggested?.(patch);
                return;
              }
              /* One argument unless the row actually has a cleared value to
                 declare — a bare `undefined` second arg is noise every host
                 and every assertion would have to know to ignore. */
              if (flagClear) onToggleRejectChange(rejectKey, flagClear.value);
              else onToggleRejectChange(rejectKey);
            }
          : undefined;

      /* Rule 7: keyed by the row's own LABEL, scoped by the surface. */
      const popoverKey = `${popoverScope}:${spec.label}`;

      /*
        **Rule 5 — a row the grid renders as a CONTROL takes a badge instead of
        ink** (VALE-1208).

        The treatment marks a changed field by inking its VALUE, which works
        only where the value is text the reader sees. A `boolean` renders a
        Yes|No segmented pair, a `select` renders a status pill, and an unset
        field renders the grey `Set …` placeholder — on any of those the ink
        lands on something the row does not draw, so the change goes completely
        unmarked. `badge` is the shared slot for exactly this case.
      */
      const rendersAsText = rendersValueAsText(spec);
      const text = typeof spec.display === 'string' ? spec.display : '';
      /**
       * **What the card states the value IS** (VALE-1264).
       *
       * The row's own drawn text where it has one, and otherwise the value the
       * AI *proposed* — an empty row can still be carrying a real suggestion,
       * and the card is the only place a reviewer can read it or turn it down.
       *
       * **Never the row's label**, which is what it fell back to: hovering an
       * empty `Preferred name` in the drawer produced a card reading
       * `NEW · Preferred name`, stating the field's own name where the value
       * goes and crediting the AI with having supplied it.
       */
      const stated =
        text || (found ? describeChangeValue(found.change.proposed) : '');

      /*
        **Rule 5a's other half — nothing drawn AND nothing proposed is no mark**
        (VALE-1264). The two cases an empty row can be in are genuinely
        different, and collapsing them is what produced both of this ticket's
        wrong answers in turn. A row the diff names has a suggestion to show,
        so it keeps its popover and its reject. A field of a CREATED item that
        the extraction never mentioned has neither: there is no value to state,
        nothing to reject, and a card there can only invent something. That is
        every empty row of a created contact's drawer.
      */
      if (!holdsValue && !stated) return spec;

      /**
       * **Rule 7 — one popover for both row kinds.**
       *
       * A text row takes it through `display`; a control row through
       * `valueWrapper`, because that row draws a segmented control and never
       * reads `display`. Hover, provenance and the reject are then identical on
       * every changed field rather than existing only on the rows that happen
       * to render text — returning early for a control row is what dropped the
       * reject from every toggle and select (VALE-1208).
       */
      /*
        **No suggestion, no card** (VALE-1617) — see
        {@link AiReviewFieldMarkingOptions.offersReview}. Gated here rather than
        at each of the three return paths below, so a row kind added later
        cannot be the one that goes on offering a review of the reviewer's own
        typing. The ink, the badge and the superseded value are untouched.
      */
      /*
        **The change ink belongs to the CONTROL, not to the row's whole value
        slot** (VALE-1654).

        Rule 5b reaches a kept control the only way it can from outside, a
        descendant selector — `.wrapper *` at (0,1,1) against the control's own
        (0,1,0). Applied to the whole slot it also reached the value sitting
        BESIDE the control: the superseded value, which draws muted and struck
        to say it is being replaced, came out in the amber UPDATED ink at
        `font-medium`, i.e. dressed as the value being proposed.

        {@link strikeSuggestion} below is VALE-1690's answer to the same shape
        on the REJECTED side, and it is the better one — it wraps the node the
        row drew and leaves the note a sibling. This is the unrejected half,
        which that change left on the wrapper and which still repainted the
        note. Hence the `!rejected` gate: the two halves do not overlap, and
        `strikeSuggestion` owns the rejected one.
      */
      const controlInk =
        spec.render && !rejected
          ? changeInkDescendantClassName(kind)
          : undefined;

      /**
       * **The AI's suggestion, struck WHERE THE ROW DREW IT — never the whole
       * value cell** (VALE-1690).
       *
       * The rejected treatment used to go on the cell: `valueClassName` for a
       * pill or a figure, the control wrapper for a kept picker. The applying
       * value is stated *inside* that cell — it has to be, because the popover
       * anchors there — so it was a descendant of the strike and came out
       * struck and muted along with the suggestion. A reviewer who rejected a
       * bank account's `DOD value` was then left with a row where BOTH figures
       * were crossed out and nothing at all read as the value about to be
       * written, which is the one question the row exists to answer.
       *
       * **It cannot be undone from the note's own side, which is why this is a
       * structural change rather than a class one.** `text-decoration` is
       * painted by an ancestor across its in-flow descendants and a descendant
       * cannot switch it off; `[&_*]:text-muted-foreground` outranks the note's
       * own `text-foreground` on specificity as well. Escaping the strike means
       * not being inside it.
       *
       * So the ink wraps the node the ROW drew and stops there, and the note
       * sits beside it as a sibling. That is also what the treatment always
       * meant — {@link REJECTED_VALUE_INK} describes the AI's suggestion, not
       * the cell it happens to be drawn in.
       *
       * A TEXT row is untouched: {@link ChangedValue}'s `reverted` branch draws
       * both halves itself and has read correctly since VALE-1208. A FLAG row is
       * untouched too: it moves its control to the value that will apply rather
       * than striking anything (VALE-1375), which is why `flagClear` gates this.
       */
      const strikeSuggestion = (drawn: ReactNode, fill: boolean) =>
        rejected && !flagClear && !rendersAsText ? (
          <span
            className={cn(
              /*
                **It gives way first**, which is what {@link ChangedValue}'s
                `reverted` branch already does with `shrink-[100]` on the struck
                half: the value no longer being taken is the one to lose room.
                `overflow-hidden` because the row draws its own markup in here
                and a node has nothing for `text-overflow` to ellipsise — it
                clips rather than running over the value beside it.
              */
              'flex min-w-0 shrink-[100] items-center overflow-hidden',
              /*
                A kept control fills the column, and the anchor's own fill rule
                now lands on THIS span rather than on the control — so the span
                passes it on, or the picker shrink-wraps to its own text and the
                row's click target collapses with it (VALE-1264's shape).
              */
              fill &&
                'w-full min-w-0 [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1',
              REJECTED_VALUE_INK
            )}
          >
            {drawn}
          </span>
        ) : (
          drawn
        );

      const withPopover = (node: ReactNode, fill = false) => {
        const drawn = strikeSuggestion(node, fill);
        return !offersReview ? (
          drawn
        ) : (
          <ChangePopover
            open={openKey === popoverKey}
            onMouseEnter={() => hoverOpen(popoverKey)}
            onMouseLeave={hoverClose}
            kind={kind}
            value={stated}
            /*
            The row draws its own value, so a new value's card can shrink to the
            reject (VALE-1584). `holdsValue` rather than `text`, because a
            control-drawn row — a pill, a lit segment — shows its value without
            rendering `display` as text, and that is still the reader seeing it.
            An unset row draws the placeholder and keeps the full card, where
            `stated` is the AI's proposal and the only sight of it (VALE-1264).
          */
            valueDrawnOnRow={holdsValue}
            previous={previous}
            /*
            **No source on a FIELD's popover** (VALE-1208). Provenance is per
            item on the wire — there is no per-field quote and no per-field file
            — so anything shown here would be the item's, attributed to one of
            its values. The item's own popover carries it, where it is true.
          */
            onReject={reject}
            rejected={rejected}
            /* Say what the click will actually do (VALE-1617). */
            restoresSuggested={restoresSuggested}
            /*
            The wording is the popover's, derived from the level and the kind
            (VALE-1375) — this used to restate the field-level pair here while
            two row mounts restated the item-level pair, which is four strings
            in three files describing one rule.
          */
            level="field"
          >
            <ChangeAnchor
              popoverKey={popoverKey}
              onOpen={() => hoverOpen(popoverKey)}
              onClose={hoverClose}
              closeIfOpen={closeIfOpen}
              fill={fill}
            >
              {/*
              **Rejected, the roles swap — POSITIONS INCLUDED** (VALE-1690).
              `ChangedValue` has always done this on a text row: unrejected it
              prints the AI's value then the superseded one struck, and rejected
              it prints them the other way round, so the value that will apply
              leads the row in both states. A control row stated the same two
              facts but never moved them, and the row then read differently from
              the text row beside it for no reason a reviewer could see — the
              leading slot meaning "what will apply" on one row and "what was
              turned down" on the next. One rule, one position.

              **A rejected FLAG needs no such note** (VALE-1375): the control
              itself moves to the value that will apply, so a second copy of it
              beside the control would say the same thing twice. Only the AI's
              superseded suggestion is stated, struck, and only when there is
              one to state.
            */}
              {!rendersAsText && rejected && !flagClear && !!previous && (
                <span
                  data-ai-change-note=""
                  className="text-foreground min-w-0 truncate text-xs"
                >
                  {previous}
                </span>
              )}
              {/*
              **The AI's answer, struck where the control draws it**
              (VALE-1375). The wrapper is the only place this can go:
              `valueClassName` reaches the LIT segment alone — that is how the
              change ink colours the chosen value — so a strike put there could
              never reach the segment the reviewer has just moved off.
            */}
              {strikeSupersededSegment ? (
                <span className="[&_[data-state=off]]:line-through [&_[data-state=off]]:decoration-1 [&_[data-state=off]]:opacity-70">
                  {drawn}
                </span>
              ) : (
                drawn
              )}
              {/* Rule 6 on a control row: a pill and a segmented control have
                nowhere inside to put the superseded value, so it sits beside
                what the row drew. */}
              {!rendersAsText && kind === 'updated' && !rejected && (
                <PreviousValue value={previous} />
              )}
            </ChangeAnchor>
          </ChangePopover>
        );
      };

      /*
        **Rule 9 — the `×` rejects instead of clearing** (VALE-1247). See
        `AttributeSpec.clearOverride`: on a changed row a destructive clear wrote
        an empty value through the commit path, and on a created item the row
        then failed the review list's has-a-value filter and vanished, taking the
        only way back with it. A reject says the same thing reversibly, and the
        popover already offers `Use suggested value` to undo it. `null` once
        rejected: there is nothing left to clear.
      */
      const clearOverride = reject ? (rejected ? null : reject) : undefined;

      /*
        **Rule 5b — a row that draws its own CONTROL is marked too** (VALE-1181).

        `AttributeSpec.render` hands the whole value slot to a kept control — an
        institution search, a ticker lookup — and `AttributeGrid` then skips the
        read-out, the badge, `valueClassName` and `markWrapper` along with it. So
        every mark the treatment has missed such a row entirely: the row that
        NAMES the bank, the fund or the registry was the one row on the surface
        that could not say the AI had changed it, and it had no popover and no
        reject either.

        It is marked here rather than by each surface because a kept control is
        not one surface's idea — the inventory drawer, the review's detail block
        and the two adoptions still to come all render them, and the first
        attempt put this in the block and got a tag where every other row had a
        colour. Wrapping `render` reaches the control's own text, so the ink
        cascades exactly as it does into a pill, and the popover anchors on what
        the reader can see is marked.
      */
      if (spec.render) {
        const control = spec.render;
        return {
          ...spec,
          clearOverride,
          render: () => (
            /*
              `w-full min-w-0` because the wrapper is what the cell measures:
              sized to its content it let a long institution name run past the
              column's right edge instead of truncating inside it, which the
              control's own name span is already set up to do.
            */
            /*
              Layout only. The change ink is on the control just inside, and the
              rejected ink is applied by `strikeSuggestion` inside the popover —
              neither belongs on a wrapper that also contains the notes beside
              the control (VALE-1654, VALE-1690).
            */
            <span className="flex w-full min-w-0 items-center">
              {withPopover(
                /*
                  **Wrapped HERE, not inside the popover's anchor** (VALE-1654).

                  `withPopover` returns the bare node when the row offers no
                  review, and a row the REVIEWER added offers none — nobody
                  proposed its values, so there is nothing to accept or reject
                  (`offersReview`, VALE-1617). Putting the ink inside the anchor
                  therefore dropped it from exactly those rows: a newly added
                  share holding carried its `NEW` badge and drew its ticker and
                  its registry in ordinary black, so the row's header and its
                  body disagreed.

                  The first-child pass-through continues `strikeSuggestion`'s
                  own `fill` chain through this span, so a picker still occupies
                  its cell rather than shrink-wrapping to its text (VALE-1264).
                */
                <span
                  className={cn(
                    'flex min-w-0 flex-1 items-center',
                    '[&>*:first-child]:min-w-0 [&>*:first-child]:flex-1',
                    controlInk
                  )}
                >
                  {control()}
                </span>,
                true
              )}
            </span>
          )
        };
      }

      if (!rendersAsText) {
        /*
          A `select` draws its value as a status pill and a segmented control
          draws a lit segment; both take the ink through `valueClassName`, so
          they read as changed rather than needing a badge to say it
          (VALE-1208). The badge is the fallback for a row that cannot be inked
          at all — an unset placeholder.

          **A figure row is inkable too, and inventory is where that mattered**
          (VALE-1181). `valueWithConfirmation` and `marketFigure` draw their
          amount as a NODE — the figure plus, where there is one, a provenance
          or breakdown line beneath it — so `rendersValueAsText` reads them as
          un-inkable and they fell to the badge. On screen that put a changed
          `$42,150.00` in ordinary black beside an `UPDATED` pill, while every
          other changed value on the same block was amber: the one encoding a
          reader has just been given a legend for, broken on the row the legend
          matters most for. `valueClassName` lands on the cell the node renders
          inside, so the colour reaches the figure exactly as it reaches a pill.

          These rows are unreachable on a contact, which is why the gap survived
          the extraction — and every one of the 22 inventory types has at least
          one.
        */
        /*
          **Rule 5a — a valueless row is not inkable either** (VALE-1264). An
          unset `select` draws the grey `Set …` placeholder rather than a pill,
          so the ink lands on the prompt and colours it as though the AI had
          answered the question.

          It gates the figure kinds too, and `rowHoldsAValue` already answers
          for them correctly: a node draws itself, so it counts.
        */
        const inkable =
          holdsValue &&
          (isFlagRow(spec) ||
            INKABLE_EDITOR_KINDS.includes(spec.editor?.kind ?? ''));
        return {
          ...spec,
          /*
            **A rejected FLAG draws the value that will actually apply**
            (VALE-1375). Every other rejected row states its outcome — a text
            row prints the surviving value and strikes the AI's beside it — but
            a flag drew a struck `Yes` and left the reviewer to infer that `No`
            was what would land, which is the screen disagreeing with apply time
            on the one surface built to stop exactly that. The control moves
            instead, and the AI's superseded answer is stated struck beside it.

            It is the `draft` that moves, not a second control, so the row stays
            the row: still editable, still committing through its own path, and
            un-rejecting puts the AI's answer straight back.
          */
          /*
            **While rejected, unconditionally — the NEWEST gesture wins**
            (VALE-1375, found at sign-off). This was gated on `!edited`, so a
            flag the reviewer had touched at any point could never move again:
            the reject landed, the control stayed put, and the strike below
            landed on the unlit segment — claiming the reviewer turned down the
            answer that was in fact about to apply. The gate existed to stop a
            different failure (forcing the draft made a click on the control
            snap straight back, reading as read-only), and that is now solved at
            the right layer: the hosts' `commitFields` UN-REJECTS a key the
            reviewer answers, so by the time an answer renders, `rejected` here
            is already false and there is nothing to fight. Reject after answer
            moves the control; answer after reject clears the reject.
          */
          ...(rejected && flagClear ? { draft: flagClear.draft } : {}),
          clearOverride,
          /*
            **A rejected control row reads as rejected** (VALE-1181, rule 8's
            rendering half). It kept the change ink, so a turned-down pill or
            segment went on looking like the value about to be written and the
            only thing that moved on screen was the popover's own label. A text
            row has said this since VALE-1208; every other row kind said
            nothing.

            **A rejected flag is the exception, now that it moves.** Striking it
            would strike the value the record IS about to hold, and dimming the
            whole segmented control read as disabled rather than as rejected —
            which is how it was reported.
          */
          /*
            **A rejected row drops back to its own classes here** (VALE-1690).
            {@link REJECTED_VALUE_INK} used to land on this slot, which is the
            whole value cell: the pill or the figure was struck, and so was the
            applying-value note the anchor draws beside it, leaving a row with
            no value in ordinary ink at all. {@link strikeSuggestion} wraps the
            drawn value inside the anchor instead, so the strike reaches exactly
            the AI's suggestion and stops.
          */
          valueClassName: rejected
            ? spec.valueClassName
            : inkable
              ? changeInkClassName(kind)
              : spec.valueClassName,
          /*
            **A field is marked by COLOUR, never by a tag** (VALE-1181), and the
            row keeps whatever badge it had of its own.

            The tag was the fallback for a row the ink could not reach, and it
            has been narrowing ever since: VALE-1247 took it off an inked row,
            because the ink already draws the distinction and the section legend
            names it; VALE-1264's rule 5a took it off a valueless row, because a
            `NEW` beside `Set email…` claims the AI supplied something it did
            not. What was left was a chip on scattered rows and a colour on the
            rest — one encoding for one fact, applied two ways, and the badge
            slot is a place the row was already using to say something of its
            own (`Verified` on a bank account).

            So the ink is the whole treatment for a field. An item-level
            NEW/UPDATED still reads as a tag on the row and in the drawer
            header, where there is no value to colour.
          */
          badge: spec.badge,
          /*
            **`markWrapper`, not `valueWrapper`** (VALE-1264). Both reach a
            control row, and the difference is where they sit: `valueWrapper`
            goes OUTSIDE the value button, so hanging the popover off it
            shrink-wrapped the button to the pill and took the row's click
            target down with it — a picker's whole value column tinted on hover
            and only its 65px pill responded to a click. The inner slot leaves
            the button filling the column and wraps just the mark, which is the
            shape a text row already has (its popover rides `display`, inside
            the button). So the tint and the click agree again, and the card
            still opens over the thing the reader can see is marked.
          */
          markWrapper: withPopover
        };
      }

      /*
        The treatment rides the row's `display` — the read-out it shows when it
        is not being edited — so the field stays fully editable underneath. Rule
        6 puts the old value inline beside the new one, struck; the ink says
        which state it is. A REJECTED row keeps its place and states what it
        will apply, with the AI's suggestion underneath.
      */
      return {
        ...spec,
        clearOverride,
        display: withPopover(
          <ChangedValue
            kind={kind}
            value={text}
            previous={previous}
            showsRecordedNote={showsRecordedNote}
            reverted={rejected}
            /*
              **A prose row wraps; every other row still ellipsises**
              (VALE-1698, made structural by VALE-1744).

              VALE-1698 met this as AI Scribe's note for an existing item inked
              into a fixed line, reading `…confirm before distributi…` — a word
              cut in half, which says the app failed rather than that the text
              continues. It fixed it with an opt-in `valueWraps` the surface
              declared, because the obvious derivation then was the row's
              `fullWidth`, and two shipped rows already set that (the contact's
              bank account, the inventory drawer's address) so deriving would
              have rewrapped them too.

              `multiline` is the flag that actually means what is wanted. It
              already declares "this field is prose" — it is what puts the
              editor in a textarea rather than a line — and every row in the app
              that sets it is a Notes row. So the wrap follows the row's own
              declaration rather than a second one each surface must remember,
              and the two `fullWidth` rows VALE-1698 protected are untouched,
              because neither is `multiline`.

              This became load-bearing rather than tidy when VALE-1744 folded
              the notes into the contact and asset lists: those rows are marked
              by `CompactContactDetail` and `CompactInventoryDetail`, shared
              surfaces that cannot reasonably be asked to know one of their
              fields happens to be carrying a Scribe note today. The
              surface-level option had no consumer left once VALE-1744 deleted
              the notes section, so it is gone rather than left dead.
            */
            wraps={spec.multiline === true}
          />
        )
      };
    },
    [
      showsChanges,
      changeFor,
      rejectKeyPolicy,
      wasEdited,
      itemIsNew,
      acceptedChanges,
      onToggleRejectChange,
      onRestoreSuggested,
      originalValues,
      offersReview,
      showsRecordedNote,
      popoverScope,
      openKey,
      hoverOpen,
      hoverClose,
      closeIfOpen
    ]
  );

  return useMemo(
    () => ({
      mark,
      changeFor,
      wasEdited,
      showsChanges,
      itemIsNew,
      closeHover: hoverClose
    }),
    [mark, changeFor, wasEdited, showsChanges, itemIsNew, hoverClose]
  );
}
